import React, { useState, useRef, useEffect } from 'react'

import { useTranslation } from 'react-i18next'

// MUI Imports
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  useMediaQuery,
  Typography,
  IconButton,
  CircularProgress,
  Box,
  Collapse
} from '@mui/material'
import {
  Send,
  Close
} from '@mui/icons-material'

// Component Imports
import RichTextEditor from '../lexical/RichTextEditor'
import CustomAutocomplete from '../atomic/atoms/CustomAutocomplete'
import CustomTextField from '../atomic/atoms/CustomTextField'

import { emailRegExp } from '../../utils/constants'

const emailValidation = (val) => emailRegExp.test(val)

const fieldInputStyles = {
  '& .MuiInputBase-root': {
    '&:not(.Mui-error)': {
      borderColor: 'transparent !important'
    },
    borderWidth: '1px !important',
    paddingTop: '0px !important',
    paddingBottom: '0px !important',
    '&.MuiAutocomplete-inputRoot.MuiInputBase-sizeSmall': {
      paddingBlock: 'unset !important',
      paddingInlineStart: 0
    },
    '&.Mui-focused': {
      boxShadow: 'none !important'
    },
    '& .MuiFilledInput-input': {
      padding: '2.5px 4px !important'
    }
  }
}

const ComposeMail = props => {
  const {
    title,
    openCompose,
    setOpenCompose,
    isBelowSmScreen,
    onSubmit,
    submitting,
    initialEmail,
    focusSubjectAfterInit,
    replyMessageId
  } = props

  const { t } = useTranslation()
  const isBelowMdScreen = useMediaQuery(theme => theme.breakpoints.down('md'))
  const isFullScreen = isBelowSmScreen || isBelowMdScreen

  const [visibility, setVisibility] = useState({ cc: false })
  const [toField, setToField] = useState([])
  const [ccField, setCcField] = useState([])
  const [subject, setSubject] = useState('')
  const [toFieldErr, setToFieldErr] = useState(false)
  const [subjectErr, setSubjectErr] = useState(false)

  const initialHtml = initialEmail?.html

  const toggleVisibility = value => {
    setVisibility(prev => ({ ...prev, [value]: !prev[value] }))
  }

  const exportHtmlRef = useRef()

  const successCallback = () => {
    setOpenCompose(null)
  }

  const handleSubmit = () => {
    if (toField?.length <= 0) {
      setToFieldErr(true)
    } else if (!subject) {
      setSubjectErr(true)
    } else {
      const exportedHtml = exportHtmlRef.current.exportHtml()

      onSubmit(
        {
          subject,
          to: toField,
          cc: ccField,
          html: exportedHtml
        },
        successCallback,
        replyMessageId
      )
    }
  }

  useEffect(() => {
    if (openCompose) {
      setToField(initialEmail?.from_address
        ? [initialEmail?.from_address]
        : [])
      setCcField(initialEmail?.cc?.length
        ? [...initialEmail.cc]
        : [])
      setSubject(initialEmail?.subject || '')
      setToFieldErr(false)
      setSubjectErr(false)

      if (focusSubjectAfterInit) {
        setTimeout(() => {
          const elem = document.getElementById('focusWorkaroundId')
          elem && elem.focus()
        }, 100)
      }
    }
  }, [openCompose, initialEmail])

  return (
    <Dialog
      open={!!openCompose}
      onClose={() => setOpenCompose(null)}
      maxWidth="md"
      fullWidth
      fullScreen={isFullScreen}
      PaperProps={{
        sx: {
          borderRadius: isFullScreen
            ? 0
            : 3,
          height: isFullScreen
            ? '100%'
            : 'auto',
          width: isFullScreen
            ? '100%'
            : undefined,
          maxWidth: isFullScreen
            ? '100% !important'
            : undefined,
          maxHeight: isFullScreen
            ? '100%'
            : 'calc(100vh - 96px)',
          m: isFullScreen
            ? 0
            : undefined,
          my: isFullScreen
            ? 0
            : 6,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 3,
        py: 2.5,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Typography sx={{ fontWeight: 600, fontSize: '1.05rem', color: 'text.primary' }}>
          {title}
        </Typography>
        <IconButton
          size="small"
          onClick={() => setOpenCompose(null)}
          sx={{ color: 'text.secondary' }}
        >
          <Close sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogTitle>

      {/* Fields */}
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* To field */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 3,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: 'text.disabled', flexShrink: 0, width: 60 }}>
            {t('mailTemplate.mailbox.send.toLabel')}
          </Typography>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <CustomAutocomplete
              multiple
              options={[]}
              value={toField}
              freeSolo
              autoSelect
              fullWidth
              onChange={(e, val) => {
                if (e.target.value === undefined || emailValidation(e.target.value)) {
                  setToField(val)
                  setToFieldErr(false)
                }
              }}
              getOptionLabel={(option) => option}
              renderInput={(params) => (
                <CustomTextField
                  {...params}
                  sx={fieldInputStyles}
                  value={toField}
                  error={toFieldErr}
                />
              )}
            />
          </Box>
          <Typography
            onClick={() => toggleVisibility('cc')}
            sx={{
              fontSize: '0.8rem',
              color: 'text.secondary',
              cursor: 'pointer',
              flexShrink: 0,
              '&:hover': { color: 'primary.main' }
            }}
          >
            {t('mailTemplate.mailbox.send.cc')}
          </Typography>
        </Box>

        {/* CC field */}
        <Collapse in={visibility.cc}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 3,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: 'text.disabled', flexShrink: 0, width: 60 }}>
              {t('mailTemplate.mailbox.send.ccLabel')}
            </Typography>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <CustomAutocomplete
                multiple
                options={[]}
                value={ccField}
                freeSolo
                autoSelect
                fullWidth
                onChange={(e, val) => {
                  if (e.target.value === undefined || emailValidation(e.target.value)) {
                    setCcField(val)
                  }
                }}
                getOptionLabel={(option) => option}
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    sx={fieldInputStyles}
                    value={ccField}
                  />
                )}
              />
            </Box>
          </Box>
        </Collapse>

        {/* Subject field */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 3,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: 'text.disabled', flexShrink: 0, width: 60 }}>
            {t('mailTemplate.mailbox.send.subjectLabel')}
          </Typography>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <CustomTextField
              id='focusWorkaroundId'
              fullWidth
              value={subject}
              sx={fieldInputStyles}
              onChange={(e) => {
                setSubject(e.target.value)
                if (e.target.value && subjectErr) {
                  setSubjectErr(false)
                }
              }}
              error={subjectErr}
            />
          </Box>
        </Box>

        {/* Rich text editor */}
        <Box sx={{ flex: 1, minHeight: 200, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          <RichTextEditor
            templateHtml={initialHtml}
            ref={exportHtmlRef}
            textareaStyles={{
              minHeight: 180,
              resize: 'none'
            }}
          />
        </Box>
      </DialogContent>

      {/* Footer */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        px: 3,
        pt: 4,
        pb: 4,
        gap: 1,
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: '#fafafa',
        flexShrink: 0
      }}>
        <Button
          variant="outlined"
          onClick={() => setOpenCompose(null)}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 500,
            px: 3
          }}
        >
          {t('mailTemplate.mailbox.send.cancel', 'Cancel')}
        </Button>
        <Button
          variant="contained"
          endIcon={submitting
            ? <CircularProgress sx={{ color: '#fff' }} size={18} />
            : <Send sx={{ fontSize: 18 }} />
          }
          onClick={handleSubmit}
          disabled={submitting}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3
          }}
        >
          {t('mailTemplate.mailbox.send.send')}
        </Button>
      </Box>
    </Dialog>
  )
}

export default ComposeMail
